'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useProjectsData } from '@/hooks/useProjectsData';
import { DC_REFRESH_EVENT } from '@/lib/dashboard/header-config';
import { ProjectRecord, ProjectsFiltersState } from '@/lib/projects/project-types';
import { applyFiltersAndSort, buildActiveFilterChips, removeChip } from '@/lib/projects/project-utils';
import { getFailedSources } from '@/lib/projects/source-status';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { ExportDropdown } from '@/components/projects/ExportDropdown';
import { ProjectFilters } from '@/components/projects/ProjectFilters';
import { ProjectTable } from '@/components/projects/ProjectTable';
import { ProjectDetailSheet } from '@/components/projects/ProjectDetailSheet';
import { ProjectsPageSkeleton } from '@/components/projects/states/ProjectsPageSkeleton';
import { ProjectsErrorState } from '@/components/projects/states/ProjectsErrorState';
import { ProjectsEmptyState } from '@/components/projects/states/ProjectsEmptyState';
import { PartialFailureBanner } from '@/components/projects/states/PartialFailureBanner';

const defaultFilters: ProjectsFiltersState = {
  searchQuery: '',
  selectedCountries: [],
  selectedOrganizations: [],
  selectedSectors: [],
  selectedProductGroups: [],
  cfpMode: 'any',
  sortMode: 'relevance',
};

export default function ProjectsPage() {
  const { projects, sourceStatuses, isLoading, error, refetch } = useProjectsData();
  const { searchQuery, clearSearch } = useHeaderSearch({
    placeholder: 'Search countries, organisations, sectors...',
    ariaLabel: 'Search projects',
  });

  const [filters, setFilters] = useState<ProjectsFiltersState>(defaultFilters);
  const [selectedProject, setSelectedProject] = useState<ProjectRecord | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const visibleProjects = useMemo(
    () => applyFiltersAndSort(projects, { ...filters, searchQuery }),
    [projects, filters, searchQuery]
  );

  // Keep refs so event handlers always see latest values
  const visibleProjectsRef = useRef(visibleProjects);
  const isLoadingRef = useRef(isLoading);

  useEffect(() => {
    visibleProjectsRef.current = visibleProjects;
  }, [visibleProjects]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Listen for refresh event from layout header
  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DC_REFRESH_EVENT, handler);
    return () => window.removeEventListener(DC_REFRESH_EVENT, handler);
  }, [refetch]);

  const chips = useMemo(() => buildActiveFilterChips(filters), [filters]);

  const orderedIds = useMemo(() => visibleProjects.map((p) => p.id), [visibleProjects]);

  const currentIndex = selectedProject ? orderedIds.indexOf(selectedProject.id) : -1;

  const failedSources = useMemo(() => getFailedSources(sourceStatuses), [sourceStatuses]);

  const handleSelectProject = useCallback((project: ProjectRecord) => {
    setSelectedProject(project);
    setSheetOpen(true);
  }, []);

  const handleNavigate = useCallback(
    (nextIndex: number) => {
      const nextProject = visibleProjects[nextIndex];
      if (nextProject) {
        setSelectedProject(nextProject);
        setSheetOpen(true);
      }
    },
    [visibleProjects]
  );

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    clearSearch();
  };

  const handleRemoveChip = (chipKey: string) => {
    setFilters((prev) => removeChip(prev, chipKey));
  };

  const handleTryExample = (patch: Partial<ProjectsFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const hasActiveFilters =
    filters.searchQuery !== '' ||
    filters.selectedCountries.length > 0 ||
    filters.selectedOrganizations.length > 0 ||
    filters.selectedSectors.length > 0 ||
    filters.selectedProductGroups.length > 0 ||
    filters.cfpMode !== 'any' ||
    filters.sortMode !== 'relevance';

  // Loading state: full page skeleton
  if (isLoading) {
    return <ProjectsPageSkeleton />;
  }

  // Hard error state
  if (error) {
    return (
      <ProjectsErrorState
        error={error}
        onRetry={refetch}
        sourceStatuses={sourceStatuses}
      />
    );
  }

  return (
    <div className="p-4 lg:p-6">

      <ProjectFilters
        projects={projects}
        value={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
        chips={chips}
        onRemoveChip={handleRemoveChip}
        rightSlot={
          <ExportDropdown
            getRows={() => visibleProjectsRef.current}
            isLoadingRef={isLoadingRef}
            metadata={{
              filters,
              searchQuery,
              totalProjectsCount: projects.length,
            }}
          />
        }
      />

      {/* Partial failure banner */}
      {failedSources.length > 0 && !bannerDismissed && (
        <PartialFailureBanner
          failed={failedSources}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      <ProjectTable
        projects={visibleProjects}
        onSelect={handleSelectProject}
        resultsCount={visibleProjects.length}
        emptyState={
          <ProjectsEmptyState
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            onTryExample={handleTryExample}
          />
        }
      />

      <ProjectDetailSheet
        project={selectedProject}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        context={{ orderedIds, currentIndex, onNavigate: handleNavigate }}
      />
    </div>
  );
}
